import { useState, useEffect } from 'react'
import type { PrinterResponse, LabelTemplateResponse } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
  LabelPreview,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'

interface PrinterForm {
  name: string
  driver: 'dymo_client' | 'zebra_browser_print'
  model: string
  connectionHint: string
  defaultTemplateId: string
  isDefault: boolean
}

const defaults: PrinterForm = {
  name: '',
  driver: 'dymo_client',
  model: '',
  connectionHint: '',
  defaultTemplateId: '',
  isDefault: false,
}

interface Props {
  readonly printerId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (p: PrinterResponse) => void
  readonly onArchived?: (id: string) => void
}

function printerToForm(p: PrinterResponse): PrinterForm {
  return {
    name: p.name,
    driver: p.driver,
    model: p.model,
    connectionHint: p.connectionHint ?? '',
    defaultTemplateId: p.defaultTemplateId ?? '',
    isDefault: p.isDefault,
  }
}

const CONNECTION_HINTS: Record<PrinterForm['driver'], string> = {
  dymo_client: 'Printer name (from DYMO Connect)',
  zebra_browser_print: 'Printer UID (from Zebra Browser Print)',
}

export function PrinterEditModal({ printerId, isOpen, isNew, onClose, onSaved, onArchived }: Props) {
  const [form, setForm] = useState<PrinterForm>(defaults)
  const [templates, setTemplates] = useState<readonly LabelTemplateResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  // Load printer data on open
  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setError(null)
      return
    }
    if (!printerId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<PrinterResponse>(`/admin/printers/${printerId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) setForm(printerToForm(res.data))
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load printer')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [printerId, isOpen, isNew])

  // Load templates filtered by the currently-selected driver
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    api
      .get<readonly LabelTemplateResponse[]>(`/admin/label-templates?driver=${form.driver}&limit=100`)
      .then((res) => {
        if (cancelled) return
        if (res.data) setTemplates(res.data)
      })
      .catch(() => {
        // non-fatal
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, form.driver])

  const update = <K extends keyof PrinterForm>(key: K, value: PrinterForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // The list endpoint (`GET /admin/label-templates`) already selects full rows
  // including `templateBody`, so the preview can read straight from the
  // already-loaded `templates` array — no separate per-id fetch needed.
  const selectedTemplate = templates.find((t) => t.id === form.defaultTemplateId) ?? null

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        driver: form.driver,
        model: form.model,
        isDefault: form.isDefault,
      }
      if (form.connectionHint) payload.connectionHint = form.connectionHint
      if (form.defaultTemplateId) payload.defaultTemplateId = form.defaultTemplateId

      if (isNew) {
        // organizationId is injected server-side from the JWT
        const res = await api.post<PrinterResponse>('/admin/printers', payload)
        if (!res.data) throw new Error('No data returned')
        if (form.isDefault) {
          await api.post(`/admin/printers/${res.data.id}/set-default`, {})
        }
        onSaved(res.data)
      } else {
        const res = await api.put<PrinterResponse>(`/admin/printers/${printerId}`, payload)
        if (!res.data) throw new Error('No data returned')
        if (form.isDefault) {
          await api.post(`/admin/printers/${printerId}/set-default`, {})
        }
        onSaved(res.data)
      }
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!printerId) return
    setArchiving(true)
    try {
      await api.post(`/admin/printers/${printerId}/archive`, {})
      onArchived?.(printerId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const title = isNew ? 'New Printer' : `Edit Printer — ${form.name}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="560px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
              <FormField name="name" label="Name" required value={form.name} onChange={(v) => update('name', v)} />
              <FormField name="driver" label="Driver" required>
                <select
                  style={input}
                  value={form.driver}
                  onChange={(e) => {
                    update('driver', e.target.value as PrinterForm['driver'])
                    // Clear template selection when driver changes in create mode
                    if (isNew) update('defaultTemplateId', '')
                  }}
                  disabled={!isNew}
                  data-testid="driver-select"
                >
                  <option value="dymo_client">DYMO (local driver)</option>
                  <option value="zebra_browser_print">Zebra Browser Print</option>
                </select>
              </FormField>
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="model" label="Model" required value={form.model} onChange={(v) => update('model', v)} />
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField
                name="connectionHint"
                label="Connection Hint"
                value={form.connectionHint}
                onChange={(v) => update('connectionHint', v)}
                placeholder={CONNECTION_HINTS[form.driver]}
              />
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <FormField name="defaultTemplateId" label="Default Template">
                <select
                  style={input}
                  value={form.defaultTemplateId}
                  onChange={(e) => update('defaultTemplateId', e.target.value)}
                  data-testid="default-template-select"
                >
                  <option value="">-- None --</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </FormField>
              {selectedTemplate && (
                <div style={{ marginTop: '0.5rem' }}>
                  <LabelPreview
                    driver={selectedTemplate.driver}
                    body={selectedTemplate.templateBody}
                    paperSize={selectedTemplate.paperSize ?? undefined}
                  />
                </div>
              )}
            </div>

            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={form.isDefault}
                  onChange={(e) => update('isDefault', e.target.checked)}
                  data-testid="is-default-checkbox"
                />
                Set as default printer for this org
              </label>
            </div>

            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                  data-testid="archive-btn"
                >
                  Archive
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                Cancel
              </button>
              <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving} data-testid="save-btn">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Printer"
        message={`Archive ${form.name}? It will no longer be available for new print jobs.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
